import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { createContext } from '@shared/utils/createContext';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { resolveSupportedLocale } from '@shared/core/i18n/utils';
import { useEventInfo } from '@shared/utils/eventInfo';
import { EventType } from '@graphql/generated-base';

/**
 * The locale guest-facing dates should format in, when it differs from the
 * event locale. Provided only on guest surfaces (guest site incl. RSVP/stays,
 * guest registry) — admin surfaces never mount the provider, so their date
 * rendering (SMS/email previews, planning tables) stays frozen to the event
 * locale via useEventLocale's fallback.
 */
const [GuestDisplayLocaleContextProvider, useGuestDisplayLocaleContext] = createContext<string | undefined>({ name: 'GuestDisplayLocale', strict: false });

/** The guest-selected display locale, or undefined outside guest surfaces / when the feature is off. */
export const useGuestDisplayLocale = (): string | undefined => useGuestDisplayLocaleContext();

export const GuestDisplayLocaleProvider: React.FC = ({ children }) => {
  // Wedding-only, like the picker itself (PRNT-4604) — skipped flags aren't exposure-tracked.
  // During SSR / initial load only ssrEventInfo is populated; without the fallback, wedding
  // guests would get event-locale dates server-side and a visible flip after hydration.
  const { eventInfo, ssrEventInfo } = useEventInfo();
  const isWeddingEvent = (eventInfo?.eventType ?? ssrEventInfo?.eventType) === EventType.wedding;
  const [isSelectorEnabled] = useIsFeatureEnabled('guestSiteLanguageSelectorEnabled', { skip: !isWeddingEvent });
  // react-i18next's i18n is request-scoped during SSR and re-renders on
  // languageChanged, so dates follow the picker the same way strings do.
  const { i18n } = useTranslation();

  const value = isSelectorEnabled && isWeddingEvent && i18n.language ? resolveSupportedLocale(i18n.language).locale : undefined;

  return <GuestDisplayLocaleContextProvider value={value}>{children}</GuestDisplayLocaleContextProvider>;
};

GuestDisplayLocaleProvider.displayName = 'GuestDisplayLocaleProvider';
